import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { Structure } from '@/shared/types/admin'

const TYPES = [
  { value: 'ECOLE', label: 'École' },
  { value: 'COLLEGE', label: 'Collège' },
  { value: 'LYCEE', label: 'Lycée' },
  { value: 'ASSOCIATION', label: 'Association' },
  { value: 'AUTRE', label: 'Autre' },
]

const structureSchema = z.object({
  nom: z.string().min(1, 'Le nom est obligatoire'),
  type: z.string().min(1, 'Le type est obligatoire'),
  ville: z.string().optional(),
})

type StructureFormValues = z.infer<typeof structureSchema>

interface StructureFormProps {
  structure?: Structure
  onSubmit: (data: StructureFormValues) => void
  isPending: boolean
}

export function StructureForm({ structure, onSubmit, isPending }: StructureFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StructureFormValues>({
    resolver: zodResolver(structureSchema),
    defaultValues: {
      nom: structure?.nom ?? '',
      type: structure?.type ?? 'AUTRE',
      ville: structure?.ville ?? '',
    },
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-lg space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">Informations de la structure</h2>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-neutral-700">
            Nom <span className="text-red-400">*</span>
          </label>
          <input
            {...register('nom')}
            type="text"
            placeholder="Ex: Lycée Victor Hugo"
            className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
          />
          {errors.nom && <p className="mt-1 text-xs text-red-500">{errors.nom.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              Type <span className="text-red-400">*</span>
            </label>
            <select
              {...register('type')}
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {errors.type && <p className="mt-1 text-xs text-red-500">{errors.type.message}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Ville</label>
            <input
              {...register('ville')}
              type="text"
              placeholder="Ex: Paris"
              className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending ? 'Enregistrement...' : structure ? 'Modifier' : 'Créer la structure'}
        </button>
      </div>
    </form>
  )
}
